/**
 * Vocabulary of the AI-team admin surface — pure mappings, no React.
 *
 * The state words come from the server (`system/services/ai_team.py` for the
 * employee state, `JobStatus` for the queue, the confirmation queue for
 * questions). This module only decides how each word is toned in the UI, so
 * the same status never reads green on one page and red on another.
 */
import type { PillTone } from "../components/Pill";

export const AGENT_STATES = [
  "working",
  "idle",
  "backlogged",
  "login_expired",
  "stuck",
  "deaf",
  "offline",
  "suspended",
] as const;

const AGENT_STATE_TONE: Record<string, PillTone> = {
  working: "ok",
  idle: "muted",
  backlogged: "signal",
  // Claude Code rejects the seat's login: nothing runs until someone types /login.
  login_expired: "alert",
  stuck: "alert",
  // Runner up, receiver not connected: it hears no new work.
  deaf: "alert",
  offline: "alert",
  suspended: "muted",
};

/** Tone for an employee state word; unknown states stay muted. */
export function agentStateTone(state: string | null | undefined): PillTone {
  return (state && AGENT_STATE_TONE[state]) || "muted";
}

/** The eight canonical `pending_jobs` states, in lifecycle order. */
export const JOB_STATUSES = [
  "submitted",
  "working",
  "input_required",
  "auth_required",
  "completed",
  "failed",
  "canceled",
  "rejected",
] as const;

const TERMINAL_JOB_STATUSES = new Set([
  "completed",
  "failed",
  "canceled",
  "rejected",
]);

/** Terminal jobs can be retried; non-terminal ones can be canceled. */
export function isTerminalJobStatus(status: string): boolean {
  return TERMINAL_JOB_STATUSES.has(status);
}

const JOB_STATUS_TONE: Record<string, PillTone> = {
  submitted: "muted",
  working: "ok",
  input_required: "signal",
  auth_required: "signal",
  completed: "ok",
  failed: "alert",
  canceled: "muted",
  rejected: "alert",
};

export function jobStatusTone(status: string): PillTone {
  return JOB_STATUS_TONE[status] || "muted";
}

const QUESTION_STATUS_TONE: Record<string, PillTone> = {
  pending: "signal",
  answered: "ok",
  denied: "muted",
  timeout: "alert",
};

export function questionStatusTone(status: string): PillTone {
  return QUESTION_STATUS_TONE[status] || "muted";
}

/** The question log's status filter, shared by the drawer tab and the page. */
export type QuestionFilter =
  | "pending"
  | "answered"
  | "denied"
  | "timeout"
  | "all";

export const QUESTION_FILTERS: { value: QuestionFilter; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "answered", label: "Answered" },
  { value: "denied", label: "Denied" },
  { value: "timeout", label: "Timed out" },
  { value: "all", label: "All" },
];

export function urgencyTone(urgency: string | null | undefined): PillTone {
  return urgency === "blocking" ? "alert" : "muted";
}

/** Hours after which a pending question's age escalates in tone. Mirrors the
 * server's reminder ladder (first Slack escalation at ~4h, daily after). */
const AGE_SIGNAL_H = 4;
const AGE_ALERT_H = 24;

/**
 * Tone for a pending question's age — the older an unanswered question, the
 * louder it reads. Resolved rows should not use this (age is only urgency
 * while an answer is still owed).
 */
export function questionAgeTone(
  createdAtIso: string | null | undefined,
  nowMs: number,
): PillTone {
  if (!createdAtIso) return "muted";
  const created = Date.parse(createdAtIso);
  if (isNaN(created)) return "muted";
  const hours = (nowMs - created) / 3_600_000;
  if (hours >= AGE_ALERT_H) return "alert";
  if (hours >= AGE_SIGNAL_H) return "signal";
  return "muted";
}

/** Drawer tabs on the AI-team page, mirrored in the `tab` search param. */
export type AgentDrawerTab = "goals" | "jobs" | "questions" | "digests" | "settings";

/**
 * Deep link into an employee's drawer on /team.
 *
 * The drawer is search-param-backed (`?agent=…&tab=…&job=…`), so every
 * question, job and employee reference across the team surface can link
 * straight to the evidence instead of naming it in prose.
 */
export function agentDrawerUrl(
  agentId: string,
  opts?: { tab?: AgentDrawerTab; jobId?: string },
): string {
  const params = new URLSearchParams({ agent: agentId });
  if (opts?.tab) params.set("tab", opts.tab);
  if (opts?.jobId) {
    params.set("tab", opts.tab ?? "goals");
    params.set("job", opts.jobId);
  }
  return `/team?${params.toString()}`;
}

/** The four review-doc states, in lifecycle order. */
export const REVIEW_STATUSES = [
  "pending",
  "changes_requested",
  "approved",
  "done",
] as const;

const REVIEW_STATUS_TONE: Record<string, PillTone> = {
  pending: "signal",
  changes_requested: "signal",
  approved: "ok",
  done: "muted",
};

export function reviewStatusTone(status: string): PillTone {
  return REVIEW_STATUS_TONE[status] || "muted";
}

/** Deep link to a review document's viewer page. */
export function reviewUrl(reviewId: string, kind?: string): string {
  return `${reviewListUrl(kind)}/${reviewId}`;
}

/** The inbox a review belongs to — each kind has its own tab. */
export function reviewListUrl(kind: string | null | undefined): string {
  if (kind === "email") return "/team/email-drafts";
  return kind === "briefing" ? "/team/briefings" : "/team/reviews";
}

/** Label for that inbox, for back links and empty states. */
export function reviewListLabel(kind: string | null | undefined): string {
  if (kind === "email") return "Email Drafts";
  return kind === "briefing" ? "Briefings" : "AI Reviews";
}

/**
 * Tone for a goal's pace pill on the team scoreboard. A goal whose metric
 * the server cannot measure (`measurement: "unavailable"`) stays muted —
 * "no data" is not the same signal as "behind".
 */
export function goalPaceTone(
  measurement: string,
  onTrack: boolean | null | undefined,
): PillTone {
  if (measurement === "unavailable" || onTrack == null) return "muted";
  return onTrack ? "ok" : "alert";
}

const VERDICT_TONE: Record<string, PillTone> = {
  cleared: "ok",
  suspicious: "signal",
  quarantined: "alert",
};

export function verdictTone(verdict: string): PillTone {
  return VERDICT_TONE[verdict] || "muted";
}

/** Compact duration — "42s", "12m", "3h 20m", "2d 4h". */
export function humanizeDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
}

/** USD with enough precision for cent-scale agent spend. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return `$${value < 1 ? value.toFixed(3) : value.toFixed(2)}`;
}

/** "slug · Name" identity line, tolerating either half being absent. */
export function agentLabel(
  agent: { agent_slug?: string | null; name?: string | null },
): string {
  return agent.agent_slug || agent.name || "(unnamed)";
}

// ---------- Team status in the top bar ----------

export interface TeamAttentionCounts {
  agents_alerting: number;
  pending_questions: number;
  pending_reviews: number;
  pending_email_drafts?: number;
  pending_briefings?: number;
}

/** Everything that waits on the owner: alerting employees, open questions,
 * pending reviews and email drafts, and the briefings to debrief or read.
 * Snoozed reviews are excluded server-side. */
export function teamNeedsYouCount(c: TeamAttentionCounts): number {
  return c.agents_alerting + c.pending_questions + c.pending_reviews + (c.pending_email_drafts ?? 0)
    + (c.pending_briefings ?? 0);
}

/** The Team area's sections – the sub-nav strip and the top-bar Team menu
 * render this one list, with the pending counts on the sections they wait in. */
export function teamSections(
  counts: TeamAttentionCounts | null,
): { to: string; label: string; end?: boolean; count?: number }[] {
  return [
    // `end` on Overview: it is the tree's index route, and a prefix match
    // would light it up on every sub-page.
    { to: "/team", label: "Overview", end: true },
    { to: "/team/questions", label: "Questions", count: counts?.pending_questions },
    { to: "/team/reviews", label: "Reviews", count: counts?.pending_reviews },
    { to: "/team/email-drafts", label: "Email drafts", count: counts?.pending_email_drafts },
    { to: "/team/briefings", label: "Briefings", count: counts?.pending_briefings },
    { to: "/team/reports", label: "Reports" },
    { to: "/team/email-trust", label: "Email trust" },
  ];
}

export type TeamPresence = "needs-you" | "working" | "idle";

/** Attention beats activity: one item waiting on the owner outranks any work. */
export function teamPresence(needsYou: number, working: number): TeamPresence {
  if (needsYou > 0) return "needs-you";
  return working > 0 ? "working" : "idle";
}

/** Tooltip line for the Team item, e.g. "2 questions · 3 reviews · 1 briefing". */
export function teamAttentionBreakdown(c: TeamAttentionCounts, working: number): string {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  add(c.pending_questions, "question", "questions");
  add(c.pending_reviews, "review", "reviews");
  add(c.pending_email_drafts ?? 0, "email draft", "email drafts");
  add(c.pending_briefings ?? 0, "briefing", "briefings");
  add(c.agents_alerting, "employee needs help", "employees need help");
  if (parts.length === 0) return working > 0 ? `${working} working` : "Nothing needs you";
  return parts.join(" · ");
}

/** P1 urgent → danger, P2 important → warn, P3 routine / none → muted. */
export function reviewPriorityTone(priority: number | null | undefined): PillTone {
  if (priority === 1) return "alert";
  if (priority === 2) return "signal";
  return "muted";
}

// Literal class names — Tailwind keeps a class only if its exact string
// appears in source.
const TONE_TEXT: Record<PillTone, string> = {
  ok: "text-ok-fg",
  signal: "text-warn-fg",
  alert: "text-danger",
  muted: "text-ink-low",
};

/** Text colour for a tone — ages and inline status words that are not pills. */
export function toneTextClass(tone: PillTone): string {
  return TONE_TEXT[tone];
}
